import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { eventFeed, EventFeedRequest, EventFeedResponse } from '@toggle/toggle';

import { wretchRequest } from '~/utils/api-fetch/apiFetch';

import {
  enrichEventsWithMarketOverviewInsights,
  fetchMarketOverviewInsights,
} from './utils';

export const QUERY_KEY_EVENT_FEED = 'QUERY_KEY_EVENT_FEED';

interface PageParam {
  page: number;
  next?: string;
  prev?: string;
}

const initialPageParam: PageParam = { page: 1 };

export const useEventFeed = (body: EventFeedRequest) => {
  const shouldFetchMarketOverview =
    body.type === undefined || body.type === 'market-overview';

  return useInfiniteQuery({
    queryKey: [QUERY_KEY_EVENT_FEED, body],
    queryFn: async ({ signal, pageParam }) => {
      const { path, schema } = eventFeed.postFeedEvents;
      const eventFeedResponse = await wretchRequest<EventFeedResponse>(path, {
        method: 'post',
        body: { ...body, ...pageParam },
        schema,
        signal,
      });

      const hasMarketOverviewEvents = eventFeedResponse.events.some(
        event => event.type === 'market-overview'
      );

      if (!shouldFetchMarketOverview || !hasMarketOverviewEvents) {
        return eventFeedResponse;
      }

      const marketOverviewData = await fetchMarketOverviewInsights(
        pageParam.page,
        signal
      );

      const enrichedEvents = enrichEventsWithMarketOverviewInsights(
        eventFeedResponse.events,
        marketOverviewData
      );

      return {
        ...eventFeedResponse,
        events: enrichedEvents,
      };
    },
    initialPageParam,
    getNextPageParam: (
      lastPage,
      _allPages,
      lastPageParam
    ): PageParam | undefined =>
      lastPage?.next_cursor
        ? { next: lastPage.next_cursor, page: lastPageParam.page + 1 }
        : undefined,
    getPreviousPageParam: (
      firstPage,
      _allPages,
      firstPageParam
    ): PageParam | undefined =>
      firstPage?.prev_cursor
        ? { prev: firstPage.prev_cursor, page: firstPageParam.page - 1 }
        : undefined,
  });
};

export const postEventRead = (eventId: string) => {
  const { path } = eventFeed.postEventRead;
  return wretchRequest(path(eventId), {
    method: 'post',
  });
};

/**
 * Hook for marking events as read with optimistic updates
 * Immediately updates the UI to show the event as read, then sends the API request
 */
export const useMarkEventRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => postEventRead(eventId),
    onMutate: async (eventId: string) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY_EVENT_FEED] });

      const previousQueries = queryClient.getQueriesData<{
        pages: EventFeedResponse[];
        pageParams: PageParam[];
      }>({ queryKey: [QUERY_KEY_EVENT_FEED] });

      queryClient.setQueriesData<{
        pages: EventFeedResponse[];
        pageParams: PageParam[];
      }>({ queryKey: [QUERY_KEY_EVENT_FEED] }, oldData => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            events: page.events.map(event =>
              event.id === eventId ? { ...event, read: true } : event
            ),
          })),
        };
      });

      return { previousQueries };
    },
    onError: (_err, _eventId, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};
